import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Share2, Facebook, MessageCircle, Link2, Check, Smartphone, Instagram } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Bouton de partage social universel.
 *
 * Stratégie OG : la page partagée pointe vers `/api/og/{kind}/{slug}` (servi par
 * le backend FastAPI avec balises Open Graph), qui redirige immédiatement
 * l'humain vers la SPA via meta-refresh. Facebook scrape l'URL et voit les OG.
 *
 * Props :
 *  - kind        : "athlete" | "club" | "coach" | "federation" | "competition"
 *                  | "curator" | "article" | "funding" | "road2027" | "timeline"
 *  - slug        : slug ou id de l'entité (ignoré pour road2027 / timeline)
 *  - title       : titre pour Web Share API (mobile natif)
 *  - text        : texte court pour Web Share API
 *  - canonicalPath : URL relative canonique (ex: "/athletes/anna-yyc")
 *  - size, variant, label, dataTestId
 */
export function ShareButton({
  kind,
  slug,
  title,
  text,
  canonicalPath,
  size = "sm",
  variant = "outline",
  label = "Partager",
  dataTestId = "share-button",
  showLabel = true,
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // L'URL partagée pointe vers /api/og/... pour que les bots sociaux
  // récupèrent les balises Open Graph. L'utilisateur final est redirigé
  // immédiatement vers la SPA via meta-refresh / JS.
  const ogPath = slug ? `/api/og/${kind}/${slug}` : `/api/og/${kind}/-`;
  const ogUrl = `${BACKEND_URL}${ogPath}`;
  const canonicalUrl = canonicalPath ? `${BACKEND_URL}${canonicalPath}` : ogUrl;

  const track = (platform) => {
    api.post("/og/track", {
      entity_type: kind,
      entity_id: slug || "",
      platform,
      url: ogUrl,
    }).catch(() => {});
  };

  const shareFacebook = () => {
    const u = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(ogUrl)}`;
    window.open(u, "_blank", "noopener,noreferrer,width=600,height=600");
    track("facebook"); setOpen(false);
  };
  const shareMessenger = () => {
    // Messenger requiert un app_id ; on retombe sur le sharer mobile
    const u = `fb-messenger://share?link=${encodeURIComponent(ogUrl)}`;
    window.location.href = u;
    setTimeout(() => track("messenger"), 100);
    setOpen(false);
  };
  const shareWhatsApp = () => {
    const msg = encodeURIComponent(`${title || "AITO.PF"} — ${ogUrl}`);
    const u = `https://wa.me/?text=${msg}`;
    window.open(u, "_blank", "noopener,noreferrer");
    track("whatsapp"); setOpen(false);
  };
  const shareTwitter = () => {
    const msg = encodeURIComponent(title || "AITO.PF — Le sport polynésien");
    const u = `https://twitter.com/intent/tweet?text=${msg}&url=${encodeURIComponent(ogUrl)}`;
    window.open(u, "_blank", "noopener,noreferrer");
    track("twitter"); setOpen(false);
  };
  const shareInstagram = async () => {
    // Instagram ne supporte pas les liens directs vers Story côté web.
    // Mobile : on utilise navigator.share (le sélecteur natif propose Instagram Story
    // sur iOS 12.2+ et Android récents).
    // Desktop : on copie l'URL et on ouvre instagram.com pour que l'utilisateur colle.
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share({ title: title || "AITO.PF", text: text || "", url: ogUrl });
        track("instagram");
      } catch { /* annulé */ }
      setOpen(false);
      return;
    }
    try {
      await navigator.clipboard.writeText(ogUrl);
      toast.success("Lien copié. Ouvrez Instagram, créez une story et collez le lien.", { duration: 5000 });
      window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
      track("instagram");
    } catch {
      toast.error("Impossible de copier le lien. Copiez-le manuellement.");
    }
    setOpen(false);
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(canonicalUrl);
      setCopied(true);
      toast.success("Lien copié dans le presse-papier.");
      setTimeout(() => setCopied(false), 2000);
      track("copy_link");
    } catch {
      toast.error("Impossible de copier le lien.");
    }
  };
  const shareNative = async () => {
    if (!navigator.share) { setOpen(true); return; }
    try {
      await navigator.share({ title: title || "AITO.PF", text: text || "", url: ogUrl });
      track("native");
    } catch (e) {
      // Annulé par l'utilisateur — pas d'erreur
    }
  };

  const hasNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  // Sur mobile (hasNativeShare), bouton direct vers l'API native
  if (hasNativeShare) {
    return (
      <Button onClick={shareNative} size={size} variant={variant} data-testid={dataTestId}>
        <Share2 className="h-4 w-4" />
        {showLabel && <span className="ml-1.5">{label}</span>}
      </Button>
    );
  }

  // Sur desktop : popover avec choix de plateforme
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size={size} variant={variant} data-testid={dataTestId}>
          <Share2 className="h-4 w-4" />
          {showLabel && <span className="ml-1.5">{label}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-2" align="end" data-testid="share-popover">
        <div className="flex flex-col gap-1">
          <Button variant="ghost" size="sm" onClick={shareFacebook} data-testid="share-facebook" className="justify-start">
            <Facebook className="h-4 w-4 mr-2 text-[#1877F2]" />Facebook
          </Button>
          <Button variant="ghost" size="sm" onClick={shareInstagram} data-testid="share-instagram" className="justify-start">
            <Instagram className="h-4 w-4 mr-2 text-[#E4405F]" />Instagram (story)
          </Button>
          <Button variant="ghost" size="sm" onClick={shareWhatsApp} data-testid="share-whatsapp" className="justify-start">
            <MessageCircle className="h-4 w-4 mr-2 text-[#25D366]" />WhatsApp
          </Button>
          <Button variant="ghost" size="sm" onClick={shareMessenger} data-testid="share-messenger" className="justify-start">
            <Smartphone className="h-4 w-4 mr-2 text-[#0084FF]" />Messenger
          </Button>
          <Button variant="ghost" size="sm" onClick={shareTwitter} data-testid="share-twitter" className="justify-start">
            <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            X / Twitter
          </Button>
          <hr className="my-1" />
          <Button variant="ghost" size="sm" onClick={copyLink} data-testid="share-copy" className="justify-start">
            {copied ? <Check className="h-4 w-4 mr-2 text-emerald-600" /> : <Link2 className="h-4 w-4 mr-2" />}
            {copied ? "Lien copié !" : "Copier le lien"}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
